import {ComponentProps, useEffect, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {Box} from '@mui/material';

import {TalentPoolListRow} from './TalentPoolListRow';
import {TalentPoolListItem} from './types/TalentPoolTypes';
import {BasicTable, TableSortConfig} from '../../../../components/BasicTable/BasicTable';
import {createBasicTableColumn} from '../../../../components/BasicTable/createBasicTableColumn';
import {TablePaginator} from '../../../../components/TablePaginator/TablePaginator';
import {greenColors} from '../../../../layouts/theme/designSystem';
import {AppConfig} from '../../../../lib/AppConfig';

interface Props {
  page: number;
  onPageChange: (page: number) => void;
  rows: TalentPoolListItem[];
  isFiltered?: boolean;
  pagination?: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
    nextPage: number | null;
    previousPage: number | null;
  };
  hasNext?: boolean;
  getNext?: () => void;
}

type RowProps = ComponentProps<typeof TalentPoolListRow>;

/** Ascending sort: pool → waiting_cv → match; unknown statuses last. */
const STATUS_SORT_RANK: Record<string, number> = {
  pool: 0,
  waiting_cv: 1,
  match: 2,
};

export const TalentPoolTable = ({
  page,
  onPageChange,
  rows,
  isFiltered = false,
  pagination,
  hasNext = false,
  getNext,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'talentPoolPage.tableColumns'});

  const itemsPerPage = AppConfig.PAGINATION_PER_PAGE;
  const totalItems = isFiltered ? rows.length : pagination?.totalCount ?? rows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    const requiredItems = safePage * itemsPerPage;
    const reportedTotal = pagination?.totalCount;
    const mayHaveMoreOnServer = reportedTotal === undefined ? Boolean(hasNext) : rows.length < reportedTotal;
    if (rows.length < requiredItems && mayHaveMoreOnServer && hasNext && getNext) {
      getNext();
    }
  }, [safePage, itemsPerPage, rows.length, hasNext, getNext, pagination?.totalCount]);

  const pagedRows = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, safePage, itemsPerPage]);

  const columns = useMemo(
    () => [
      createBasicTableColumn<RowProps>('candidate', t('candidate'), {
        sortable: true,
        getSortValue: (row) => row.candidateName ?? '',
        sx: {width: '15%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('position', t('position'), {
        sortable: true,
        getSortValue: (row) => row.campaignName ?? '',
        sx: {width: '17%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('status', t('status'), {
        sortable: true,
        getSortValue: (row) => STATUS_SORT_RANK[row.status] ?? 99,
        sx: {width: '10%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('date', t('date'), {
        sortable: true,
        getSortValue: (row) => row.date ?? null,
        sx: {width: '10%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('total_score', t('total'), {
        sortable: true,
        getSortValue: (row) => row.totalMatch ?? null,
        sx: {width: '11%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('job_score', t('job'), {
        sortable: true,
        getSortValue: (row) => row.jobMatch ?? null,
        align: 'center',
        sx: {width: '8%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('test_score', t('test'), {
        sortable: true,
        getSortValue: (row) => row.testMatch ?? null,
        align: 'center',
        sx: {width: '8%', minWidth: 0},
      }),
      createBasicTableColumn<RowProps>('culture_score', t('culture'), {
        sortable: true,
        getSortValue: (row) => row.cultureMatch ?? null,
        align: 'center',
        sx: {width: '8%', minWidth: 0},
      }),
      createBasicTableColumn('actions', '', {
        align: 'right',
        sx: {width: '13%', minWidth: 120},
      }),
    ],
    [t]
  );

  const rowData = pagedRows.map((data) => ({id: data.resourceId, ...data}));

  const initialSortConfig: TableSortConfig = {
    columnId: 'date',
    direction: 'desc',
  };

  return (
    <Box width='100%'>
      <Box data-tour='talentPool.table'>
        <BasicTable<RowProps>
          columns={columns}
          Row={TalentPoolListRow}
          rowData={rowData}
          initialSortConfig={initialSortConfig}
          hideLoadMoreFooter
          fullWidthLayout
          headerTextColor={greenColors[700]}
        />
      </Box>
      <Box data-tour='talentPool.pagination'>
        <TablePaginator
          page={safePage}
          totalItems={totalItems}
          itemsPerPage={itemsPerPage}
          translationKeyPrefix='talentPoolPage.pagination'
          onPageChange={onPageChange}
        />
      </Box>
    </Box>
  );
};
